import api from "@/lib/api";

/** Polling that pauses while the browser tab is hidden.
 *
 * Same as setInterval(fn, ms) while the page is visible. While it is hidden the ticks are skipped
 * (nobody is looking; every trading safety check runs on the server), and the moment the tab is
 * visible again fn runs at once, so the screen is current without waiting for the next tick.
 * When fn returns a promise, a tick that arrives while the previous one is still pending is skipped: a server
 * slower than the interval never gets a pile of overlapping polls (3 Nov 2026: 21 cards x 2 requests / 3 s
 * queued ~18 requests a second and starved the Analyse Trade dialog).
 * Returns the cleanup function for a useEffect. */
export function visibleInterval(fn, ms) {
  let busy = false;
  const run = () => {
    if (document.hidden || busy) return;
    const r = fn();
    if (r && typeof r.finally === "function") { busy = true; r.finally(() => { busy = false; }); }
  };
  const timer = setInterval(run, ms);
  document.addEventListener("visibilitychange", run);
  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", run);
  };
}

const inflight = new Map();

/** api.get for the shared polled feeds (trade list, system status, account summary, market
 * diagnostics). Several components ask for the same URL on their own timers; a caller that asks
 * while an identical request is still IN FLIGHT shares it instead of sending another. Nothing is
 * kept after it completes, so no answer is ever older than a fresh request would be. Each caller
 * gets its own copy of the data. Same arguments and result as api.get. */
export function sharedGet(path, config) {
  const key = `${path}?${JSON.stringify((config && config.params) || {})}`;
  let p = inflight.get(key);
  if (!p) {
    const req = config === undefined ? api.get(path) : api.get(path, config);
    p = Promise.resolve(req).finally(() => inflight.delete(key));
    inflight.set(key, p);
  }
  return p.then((r) => ({ ...r, data: r && r.data !== undefined ? JSON.parse(JSON.stringify(r.data)) : r && r.data }));
}
